import { useApp, useUi, useUiDispatch } from "@/app/AppContext";
import { useSelectedView } from "@/app/selectors";
import type { Tab } from "@/app/urlState";
import { ToggleChip } from "@/components/ToggleChip";
import { isRespondentOnly, isSuperadmin } from "@/domain/permissions";
import { useI18n } from "@/i18n/useI18n";
import { ChipFilters } from "./ChipFilters";
import { LocalFileLoader } from "./LocalFileLoader";
import { RegionSelect } from "./RegionSelect";

export function Toolbar({ tab }: { tab: Tab }) {
  const i18n = useI18n();
  const { t } = i18n;
  const { authMode, user, data } = useApp();
  const ui = useUi();
  const dispatch = useUiDispatch();
  const view = useSelectedView();

  if (authMode === "individual" && isRespondentOnly(user)) return null;
  if (tab === "system" || tab === "admin") return null;

  const showFileLoader = authMode === "shared" || isSuperadmin(user);
  const showViewSelect = tab === "matrix" || tab === "list";
  const showSearch = tab === "matrix" || tab === "list";
  const hasChips = view.chipDisplay !== "hidden";
  const showChips = hasChips && (tab !== "stats" || ui.statsFiltersVisible);
  const listLevelLabels = [t("detailsLevel0"), t("detailsLevel1"), t("detailsLevel2")];
  const listLevelTitles = [t("showMoreDetails"), t("hideMoreDetails"), t("hideMoreDetails2")];

  return (
    <div className="toolbar">
      <div id="toolbar-row-main" className="toolbar-row toolbar-row-main">
        {showViewSelect && (
          <div id="view-selector-wrapper">
            <label>
              <span id="label-view">{t("labelView")}</span>{" "}
              <select
                id="view-select"
                value={view.id}
                onChange={(event) => dispatch({ type: "setView", viewId: event.target.value })}
              >
                {data.settings.views
                  .filter((entry) => entry.visible)
                  .map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {i18n.viewLabel(entry)}
                    </option>
                  ))}
              </select>
            </label>
          </div>
        )}
        <RegionSelect />
        {showFileLoader && <LocalFileLoader />}
        {showSearch && (
          <div id="search-box-wrapper">
            <input
              type="search"
              className="search-input"
              placeholder={t("searchPlaceholder")}
              aria-label={t("searchPlaceholder")}
              value={ui.search}
              onChange={(event) => dispatch({ type: "setSearch", search: event.target.value })}
            />
            {ui.search && (
              <button
                type="button"
                className="search-clear-btn"
                title={t("searchClear")}
                aria-label={t("searchClear")}
                onClick={() => dispatch({ type: "setSearch", search: "" })}
              >
                ×
              </button>
            )}
          </div>
        )}
        {tab === "stats" && hasChips && (
          <div id="stats-toggle-wrapper">
            <ToggleChip
              active={ui.statsFiltersVisible}
              label={ui.statsFiltersVisible ? t("filtersShow") : t("filtersHide")}
              title={ui.statsFiltersVisible ? t("hideFilters") : t("showFilters")}
              onToggle={() => dispatch({ type: "toggleStatsFilters" })}
            />
          </div>
        )}
      </div>
      <div id="toolbar-row-objectives" className="toolbar-row toolbar-row-objectives">
        {showChips && <ChipFilters />}
        <div className="toolbar-objective-actions">
          {tab === "matrix" && (
            <>
              <ToggleChip
                active={ui.showTitles}
                label={ui.showTitles ? t("labelsOn") : t("labelsOff")}
                title={ui.showTitles ? t("hideLabels") : t("showLabels")}
                onToggle={() => dispatch({ type: "toggleTitles" })}
              />
              <ToggleChip
                active={ui.showIds}
                label={ui.showIds ? t("idsOn") : t("idsOff")}
                title={ui.showIds ? t("hideItemIds") : t("showItemIds")}
                onToggle={() => dispatch({ type: "toggleIds" })}
              />
            </>
          )}
          {tab === "list" && (
            <ToggleChip
              active={ui.listDetailsLevel > 0}
              label={listLevelLabels[ui.listDetailsLevel]!}
              title={listLevelTitles[ui.listDetailsLevel]!}
              onToggle={() => dispatch({ type: "cycleListDetails" })}
            />
          )}
        </div>
      </div>
    </div>
  );
}
